import { Field, FieldRow, InfoButton, SectionCard } from '../../shared/components';
import { createDefaultZoneLayouts } from '../../app/defaultProject';
import type { RoofModule, ZoneLayoutSettings } from '../../gen/ejot/roofing/v1/types';

interface ZoneLayoutEditorProps {
  module: RoofModule;
  onChange: (module: RoofModule) => void;
}

const ZONE_CLASSES = [
  { id: 'F', title: 'Corner zone F' },
  { id: 'G', title: 'Outer border zone G' },
  { id: 'H', title: 'Inner border zone H' },
  { id: 'I', title: 'Inner zone I' },
] as const;

const DEFAULT_LAYOUTS = createDefaultZoneLayouts();
const SUPPORTED_PATTERN_ID = 'single-overlap-row';
const PRODUCTION_ZONE_ID = '__MEMBRANE_PRODUCTION__';
const PRODUCTION_PATTERN_PREFIX = 'membrane-production';

interface ProductionFlags {
  width: boolean;
}

export function ZoneLayoutEditor({ module, onChange }: ZoneLayoutEditorProps) {
  const layouts = module.zone_layouts ?? [];

  const layoutFor = (zoneId: string): ZoneLayoutSettings =>
    layouts.find((layout) => layout.zone_id === zoneId)
      ?? DEFAULT_LAYOUTS.find((layout) => layout.zone_id === zoneId)
      ?? { zone_id: zoneId };

  const physicalLayouts = ZONE_CLASSES.map(({ id }) => layoutFor(id));
  const defaultProduction = DEFAULT_LAYOUTS.find((layout) => layout.zone_id === PRODUCTION_ZONE_ID);
  const existingProduction = layouts.find((layout) => layout.zone_id === PRODUCTION_ZONE_ID) ?? defaultProduction;
  const inferredWidth = Math.max(0, ...physicalLayouts.map((layout) => layout.membrane_width?.millimetres ?? 0));
  const productionWidth = existingProduction?.membrane_width?.millimetres ?? inferredWidth;
  const productionLength = existingProduction?.roll_length?.millimetres ?? 20_000;
  const overlapWidth = existingProduction?.overlap_width?.millimetres
    ?? physicalLayouts.find((layout) => layout.overlap_width)?.overlap_width?.millimetres
    ?? 120;
  const endLapWidth = existingProduction?.end_lap_width?.millimetres
    ?? physicalLayouts.find((layout) => layout.end_lap_width)?.end_lap_width?.millimetres
    ?? 120;
  const courseStartStagger = existingProduction?.course_start_stagger?.millimetres
    ?? physicalLayouts.find((layout) => layout.course_start_stagger)?.course_start_stagger?.millimetres
    ?? 5_000;
  const inferredFlags: ProductionFlags = {
    width: physicalLayouts.every((layout) => approximatelyEqual(layout.membrane_width?.millimetres, productionWidth)),
  };
  const flags = parseProductionFlags(existingProduction?.pattern_id, inferredFlags);

  const stripSharedFields = (layout: ZoneLayoutSettings): ZoneLayoutSettings => ({
    ...layout,
    overlap_width: undefined,
    roll_length: undefined,
    end_lap_width: undefined,
    course_start_stagger: undefined,
  });

  const normalise = (zoneId: string, patch: Partial<ZoneLayoutSettings>): ZoneLayoutSettings => stripSharedFields({
    ...layoutFor(zoneId),
    ...patch,
    zone_id: zoneId,
    pattern_id: SUPPORTED_PATTERN_ID,
    installation_direction: { degrees: 0, origin: 'VALUE_ORIGIN_INPUT' as const },
    additional_fixings_outside_overlap: false,
    additional_fixing_spacing: undefined,
    fixed_telescope: false,
  });

  const replaceLayout = (zoneId: string, value: ZoneLayoutSettings, source = layouts) => {
    const next = [...source];
    const index = next.findIndex((layout) => layout.zone_id === zoneId);
    if (index >= 0) next[index] = value;
    else next.push(value);
    return next;
  };

  const cleanPhysicalLayouts = (source: ZoneLayoutSettings[]) => {
    let next = source;
    for (const { id } of ZONE_CLASSES) {
      const current = next.find((layout) => layout.zone_id === id);
      if (current) next = replaceLayout(id, stripSharedFields(current), next);
    }
    return next;
  };

  const update = (zoneId: string, patch: Partial<ZoneLayoutSettings>) => {
    const next = cleanPhysicalLayouts(replaceLayout(zoneId, normalise(zoneId, patch)));
    onChange({ ...module, zone_layouts: next });
  };

  const productionValue = (nextFlags: ProductionFlags, patch: Partial<ZoneLayoutSettings> = {}): ZoneLayoutSettings => ({
    ...existingProduction,
    ...patch,
    zone_id: PRODUCTION_ZONE_ID,
    membrane_width: patch.membrane_width ?? existingProduction?.membrane_width ?? lengthInput(String(productionWidth)),
    roll_length: patch.roll_length ?? existingProduction?.roll_length ?? lengthInput(String(productionLength)),
    overlap_width: patch.overlap_width ?? existingProduction?.overlap_width ?? lengthInput(String(overlapWidth)),
    end_lap_width: patch.end_lap_width ?? existingProduction?.end_lap_width ?? lengthInput(String(endLapWidth)),
    course_start_stagger: patch.course_start_stagger ?? existingProduction?.course_start_stagger ?? lengthInput(String(courseStartStagger)),
    pattern_id: formatProductionFlags(nextFlags),
  });

  const updateProduction = (patch: Partial<ZoneLayoutSettings>, nextFlags = flags) => {
    let next = replaceLayout(PRODUCTION_ZONE_ID, productionValue(nextFlags, patch));
    const width = patch.membrane_width?.millimetres ?? productionWidth;
    if (nextFlags.width) {
      for (const { id } of ZONE_CLASSES) {
        next = replaceLayout(id, normaliseFrom(next, id, { membrane_width: lengthInput(String(width)) }), next);
      }
    }
    next = cleanPhysicalLayouts(next);
    onChange({ ...module, zone_layouts: next });
  };

  const setProductionWidthInheritance = (enabled: boolean) => {
    updateProduction({}, { width: enabled });
  };

  const membraneName = module.fixing_system?.custom_membrane?.display_name
    || module.fixing_system?.membrane_product_id
    || 'Not selected';
  const membraneProducer = module.fixing_system?.custom_membrane?.manufacturer || 'Catalogue / project selection';

  return (
    <div className="zone-layout-editor">
      <div className="zone-layout-intro">
        <InfoButton label="Information about fixing-layout inputs">
          Production roll length and general application values are roof-wide inputs. F, G, H and I contain only
          roll width and maximum fixing spacing in overlap. Equal roll widths are treated as one continuous laying
          group; wind-zone boundaries still retain their own fixing demand.
        </InfoButton>
        <span>Membrane-course fixing settings</span>
      </div>

      <SectionCard title="Membrane Production" defaultOpen>
        <FieldRow>
          <Field label="Membrane product" value={membraneName} derived />
          <Field label="Producer / source" value={membraneProducer} derived />
        </FieldRow>
        <FieldRow>
          <Field
            id="membrane-production-roll-width"
            label="Production roll width"
            unit="mm"
            type="number"
            value={lengthValue(productionWidth)}
            onChange={(value) => updateProduction({ membrane_width: lengthInput(value) })}
            help="Physical supplied roll width. Individual installation zones may use narrower strips."
          />
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={flags.width}
              onChange={(event) => setProductionWidthInheritance(event.target.checked)}
            />
            <span>Use width for below zones</span>
          </label>
        </FieldRow>
        <FieldRow>
          <Field
            id="membrane-production-roll-length"
            label="Production roll length"
            unit="mm"
            value={groupedLengthValue(productionLength)}
            onChange={(value) => updateProduction({ roll_length: groupedLengthInput(value) })}
            help="Single roof-wide stock-roll length used by cutting, end laps and remainder optimization."
          />
        </FieldRow>

        <SectionCard title="General Application Data" defaultOpen>
          <FieldRow>
            <Field
              id="membrane-production-overlap-width"
              label="Overlap width"
              unit="mm"
              type="number"
              value={lengthValue(overlapWidth)}
              onChange={(value) => updateProduction({ overlap_width: lengthInput(value) })}
            />
            <Field
              id="membrane-production-end-lap-width"
              label="End lap width"
              unit="mm"
              type="number"
              value={lengthValue(endLapWidth)}
              onChange={(value) => updateProduction({ end_lap_width: lengthInput(value) })}
            />
          </FieldRow>
          <FieldRow>
            <Field
              id="membrane-production-course-stagger"
              label="Course start stagger"
              unit="mm"
              type="number"
              value={lengthValue(courseStartStagger)}
              onChange={(value) => updateProduction({ course_start_stagger: lengthInput(value) })}
              help="Roof-wide offset for successive physical roll joints along the laying direction."
            />
          </FieldRow>
        </SectionCard>
      </SectionCard>

      {ZONE_CLASSES.map(({ id, title }) => {
        const layout = layoutFor(id);
        const prefix = `zone-layout-${id.toLowerCase()}`;
        const rollWidth = flags.width ? productionWidth : (layout.membrane_width?.millimetres ?? 0);
        const widthExceedsProduction = productionWidth > 0 && rollWidth > productionWidth + 1e-6;

        return (
          <SectionCard key={id} title={title} defaultOpen>
            <FieldRow>
              <Field
                id={`${prefix}-roll-width`}
                label="Roll width"
                unit="mm"
                type="number"
                value={lengthValue(rollWidth)}
                derived={flags.width}
                onChange={flags.width ? undefined : (value) => update(id, { membrane_width: lengthInput(value) })}
                help={flags.width ? 'Inherited from Membrane Production.' : 'Effective installed strip width.'}
              />
              <Field
                id={`${prefix}-maximum-spacing`}
                label="Maximum fixing spacing in overlap"
                unit="mm"
                type="number"
                value={lengthValue(layout.maximum_spacing?.millimetres)}
                onChange={(value) => update(id, { maximum_spacing: lengthInput(value) })}
              />
            </FieldRow>
            {widthExceedsProduction && (
              <p className="field-note field-note-blocking">
                Zone {id} width exceeds the production roll width. Reduce the zone width or increase the production width before calculation.
              </p>
            )}
          </SectionCard>
        );
      })}
    </div>
  );

  function normaliseFrom(source: ZoneLayoutSettings[], zoneId: string, patch: Partial<ZoneLayoutSettings>): ZoneLayoutSettings {
    const current = source.find((layout) => layout.zone_id === zoneId) ?? layoutFor(zoneId);
    return stripSharedFields({
      ...current,
      ...patch,
      zone_id: zoneId,
      pattern_id: SUPPORTED_PATTERN_ID,
      installation_direction: { degrees: 0, origin: 'VALUE_ORIGIN_INPUT' as const },
      additional_fixings_outside_overlap: false,
      additional_fixing_spacing: undefined,
      fixed_telescope: false,
    });
  }
}

function parseProductionFlags(value: string | undefined, fallback: ProductionFlags): ProductionFlags {
  if (!value?.startsWith(PRODUCTION_PATTERN_PREFIX)) return fallback;
  return { width: value.includes('width=1') };
}

function formatProductionFlags(flags: ProductionFlags): string {
  return `${PRODUCTION_PATTERN_PREFIX}:width=${flags.width ? 1 : 0}`;
}

function approximatelyEqual(value: number | undefined, target: number): boolean {
  return value !== undefined && Math.abs(value - target) <= 1e-6;
}

function lengthInput(value: string) {
  return { millimetres: finite(value), origin: 'VALUE_ORIGIN_INPUT' as const };
}

function groupedLengthInput(value: string) {
  return { millimetres: finite(value.replace(/[\s,]/g, '')), origin: 'VALUE_ORIGIN_INPUT' as const };
}

function lengthValue(value: number | undefined): string {
  return value === undefined ? '' : wholeNumber(value);
}

function groupedLengthValue(value: number | undefined): string {
  return value === undefined ? '' : Math.round(value).toLocaleString('en-GB');
}

function wholeNumber(value: number): string {
  return Math.round(value).toString();
}

function finite(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}
